import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Award, ChevronLeft, ExternalLink, FileText } from "lucide-react";
import { api, fmtEur, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import CountryCombobox from "../../components/CountryCombobox";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { OrderStatusBadge } from "./Orders";
import { toast } from "sonner";

export default function BuyerProfile() {
  const { id } = useParams();
  const [buyer, setBuyer] = useState(null);
  const [form, setForm] = useState(null);
  const [orders, setOrders] = useState([]);
  const [vendorConfirm, setVendorConfirm] = useState(null); // "official_vendor" | "standard"
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [b, allOrders] = await Promise.all([
      api.get(`/buyers/${id}`),
      api.get(`/admin/orders`),
    ]);
    setBuyer(b.data);
    setForm({
      first_name: b.data.first_name || "", last_name: b.data.last_name || "",
      email: b.data.email || "", phone: b.data.phone || "",
      country_code: b.data.country_code || "",
      company: b.data.company || "", vat_id: b.data.vat_id || "",
      street_address: b.data.street_address || "", address_line2: b.data.address_line2 || "",
      city: b.data.city || "", postal_code: b.data.postal_code || "",
      import_license: !!b.data.import_license, import_license_number: b.data.import_license_number || "",
      personal_discount_percent: b.data.personal_discount_percent ?? 0,
      admin_notes: b.data.admin_notes || "",
    });
    setOrders(allOrders.data.filter((o) => o.buyer_id === id));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form) return;
    setBusy(true);
    try {
      const payload = { ...form };
      payload.personal_discount_percent = Number(payload.personal_discount_percent) || 0;
      payload.import_license_number = payload.import_license ? (payload.import_license_number || null) : null;
      if (!payload.country_code) payload.country_code = null;
      const { data } = await api.patch(`/buyers/${id}`, payload);
      setBuyer(data);
      toast.success("Buyer saved");
    } catch (err) {
      toast.error(getErrorMessage(err, "Save failed"));
    } finally { setBusy(false); }
  };

  const toggleVendor = async () => {
    const next = vendorConfirm;
    setVendorConfirm(null);
    try {
      const { data } = await api.patch(`/buyers/${id}`, { buyer_type: next });
      setBuyer(data);
      toast.success(next === "official_vendor" ? "Marked as Official Vendor" : "Reverted to standard");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed"));
    }
  };

  const totals = useMemo(() => {
    let paid = 0, unpaid = 0, count = orders.length;
    for (const o of orders) {
      if (o.status === "cancelled") continue;
      const t = Number(o.total || 0);
      if (o.payment_status === "paid") paid += t;
      else unpaid += t;
    }
    return { paid, unpaid, count };
  }, [orders]);

  if (!buyer || !form) return <div className="p-6 text-[#5C665A]" data-testid="buyer-profile-loading">Loading…</div>;
  const isVendor = buyer.buyer_type === "official_vendor";
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <>
      <PageHeader
        title={<span data-testid="buyer-profile-name">{buyer.first_name} {buyer.last_name}</span>}
        subtitle={<>{buyer.email}{buyer.company ? ` · ${buyer.company}` : ""} · <span className="capitalize">{buyer.status}</span></>}
        testid="page-buyer-profile"
        actions={
          <>
            <Link to="/admin/buyers"><Button variant="outline" className="h-9"><ChevronLeft size={14} /> Buyers</Button></Link>
            {isVendor && <Badge className="bg-[#FBF3E8] text-[#8A5A2B] border border-[#D4A373] inline-flex items-center gap-1"><Award size={12} /> Official Vendor</Badge>}
          </>
        }
      />
      <div className="p-4 sm:p-6 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4 sm:gap-6 max-w-7xl">
        {/* Left — edit form + history */}
        <div className="space-y-4">
          <section className="bg-white border border-[#E2E4E0] rounded-lg p-5">
            <h3 className="db-h font-semibold mb-3">Profile</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="First name"><Input data-testid="bp-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-9" /></Field>
              <Field label="Last name"><Input data-testid="bp-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-9" /></Field>
              <Field label="Email"><Input data-testid="bp-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-9" /></Field>
              <Field label="Phone"><Input data-testid="bp-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-9" /></Field>
              <Field label="Company"><Input data-testid="bp-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="h-9" /></Field>
              <Field label="VAT / Tax ID"><Input data-testid="bp-vat" value={form.vat_id} onChange={(e) => set("vat_id", e.target.value)} className="h-9" /></Field>
              <Field label="Country">
                <CountryCombobox testid="bp-country" value={form.country_code} onChange={(v) => set("country_code", v)} placeholder="Select country" />
              </Field>
              <Field label="Postal code"><Input data-testid="bp-postal" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="h-9" /></Field>
              <Field label="Street address" className="md:col-span-2">
                <AddressAutocomplete
                  testid="bp-street"
                  value={form.street_address}
                  country={form.country_code}
                  onChange={(v) => set("street_address", v)}
                  onPicked={(a) => setForm((s) => ({
                    ...s,
                    street_address: a.street || s.street_address,
                    city: a.city || s.city,
                    postal_code: a.postal_code || s.postal_code,
                    country_code: a.country_code || s.country_code,
                  }))}
                  placeholder="Start typing to search"
                />
              </Field>
              <Field label="Address line 2 (optional)" className="md:col-span-2"><Input data-testid="bp-line2" value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} className="h-9" /></Field>
              <Field label="City"><Input data-testid="bp-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="h-9" /></Field>
              <Field label="Personal discount %">
                <Input data-testid="bp-discount" type="number" step="0.1" value={form.personal_discount_percent} onChange={(e) => set("personal_discount_percent", e.target.value)} className="h-9" />
              </Field>
              <div className="md:col-span-2 flex items-center gap-2">
                <input id="bp-lic" type="checkbox" data-testid="bp-license"
                       checked={form.import_license} onChange={(e) => set("import_license", e.target.checked)} />
                <label htmlFor="bp-lic" className="text-sm">Holds import licence <span className="text-xs text-[#5C665A]">(optional)</span></label>
                {form.import_license && (
                  <Input data-testid="bp-license-number" value={form.import_license_number}
                         onChange={(e) => set("import_license_number", e.target.value)}
                         placeholder="Licence #" className="h-8 flex-1" />
                )}
              </div>
              <Field label="Admin notes (internal)" className="md:col-span-2">
                <Textarea data-testid="bp-notes" value={form.admin_notes} onChange={(e) => set("admin_notes", e.target.value)} rows={2} />
              </Field>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-[#E2E4E0] pt-3">
              <button
                type="button"
                onClick={() => setVendorConfirm(isVendor ? "standard" : "official_vendor")}
                className={`inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium border transition-colors ${isVendor ? "bg-[#FBF3E8] text-[#8A5A2B] border-[#D4A373] hover:bg-[#F5E6D0]" : "bg-white text-[#1A1D1A] border-[#E2E4E0] hover:bg-[#F9F9F8]"}`}
                data-testid="bp-vendor-toggle"
              >
                <Award size={14} /> {isVendor ? "Remove Official Vendor status" : "Mark as Official Vendor"}
              </button>
              <Button onClick={save} disabled={busy} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white" data-testid="bp-save">
                {busy ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </section>

          <section className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden" data-testid="bp-order-history">
            <div className="px-4 py-3 border-b border-[#E2E4E0] flex items-center justify-between">
              <h3 className="db-h font-semibold">Order history</h3>
              <span className="text-xs text-[#5C665A]">{orders.length} order{orders.length === 1 ? "" : "s"}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[540px]">
                <thead className="bg-[#F9F9F8] text-[#5C665A]">
                  <tr>
                    <th className="text-left px-3 py-2 db-h font-semibold">Order</th>
                    <th className="text-left px-3 py-2 db-h font-semibold">Date</th>
                    <th className="text-left px-3 py-2 db-h font-semibold">Status</th>
                    <th className="text-right px-3 py-2 db-h font-semibold">Total</th>
                    <th className="text-right px-3 py-2 db-h font-semibold">Payment</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#5C665A]" data-testid="bp-order-empty">No orders yet.</td></tr>}
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-[#E2E4E0]" data-testid={`bp-order-${o.id}`}>
                      <td className="px-3 py-2 db-mono text-xs">{o.order_number}</td>
                      <td className="px-3 py-2 text-xs text-[#5C665A]">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2"><OrderStatusBadge status={o.status} /></td>
                      <td className="px-3 py-2 text-right db-mono">{fmtEur(o.total || 0)}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        <Badge className={o.payment_status === "paid" ? "bg-[#E9F5EC] text-[#1B4332] border-transparent" : "bg-neutral-100 text-neutral-600 border-transparent"}>
                          {o.payment_status || "unpaid"}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Link to={`/admin/orders/${o.id}`} className="text-[#2D6A4F] hover:underline inline-flex items-center gap-1" data-testid={`bp-order-open-${o.id}`}>
                          <ExternalLink size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Right — totals summary */}
        <aside className="space-y-4">
          <div className="bg-white border border-[#E2E4E0] rounded-lg p-4" data-testid="bp-totals">
            <div className="db-h font-semibold text-sm mb-2">Lifetime totals</div>
            <SummaryLine label="Orders" value={totals.count} mono />
            <SummaryLine label="Paid" value={fmtEur(totals.paid)} mono />
            <SummaryLine label="Unpaid" value={fmtEur(totals.unpaid)} mono highlight />
            <SummaryLine label="All-time (paid + unpaid)" value={fmtEur(totals.paid + totals.unpaid)} mono strong />
            <div className="text-[11px] text-[#5C665A] mt-2">Cancelled orders excluded. Paid = payment_status paid. Unpaid = everything else.</div>
          </div>
          <div className="bg-white border border-[#E2E4E0] rounded-lg p-4 text-sm">
            <div className="db-h font-semibold mb-2">Registered address on file</div>
            {(!buyer.street_address && !buyer.city && !buyer.postal_code) ? (
              <div className="text-[#C94A4A]">No address on file — buyer must add one to complete an invoice.</div>
            ) : (
              <address className="not-italic text-[#1A1D1A]">
                {buyer.street_address}<br />
                {buyer.address_line2 && <>{buyer.address_line2}<br /></>}
                {buyer.postal_code} {buyer.city}<br />
                {buyer.country_code}
              </address>
            )}
          </div>
        </aside>
      </div>

      <ConfirmDialog
        open={!!vendorConfirm}
        onOpenChange={(o) => !o && setVendorConfirm(null)}
        title={vendorConfirm === "official_vendor" ? "Mark as Official Vendor?" : "Remove Official Vendor status?"}
        description={vendorConfirm === "official_vendor"
          ? "This buyer will pay flat partner prices per size where set — bypassing country / tier / personal discount."
          : "This buyer returns to standard pricing (country + tier + personal discount)."}
        confirmLabel={vendorConfirm === "official_vendor" ? "Mark as Vendor" : "Revert to Standard"}
        destructive={vendorConfirm !== "official_vendor"}
        onConfirm={toggleVendor}
        testid="bp-vendor-confirm"
      />
    </>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs text-[#5C665A]">{label}</label>
      {children}
    </div>
  );
}

function SummaryLine({ label, value, mono, strong, highlight }) {
  return (
    <div className={`flex justify-between py-1 text-sm ${strong ? "border-t border-[#E2E4E0] mt-1 pt-2 font-semibold" : ""}`}>
      <span className="text-[#5C665A]">{label}</span>
      <span className={`${mono ? "db-mono" : ""} ${highlight ? "text-[#C94A4A]" : ""}`}>{value}</span>
    </div>
  );
}
